import { api } from '../../../api';
import { CrewMember } from '../../../app/entities/crew-member';
import { Project } from '../../../app/entities/project';
import { entityPool } from '../../../core/engine/engine';

export interface CrewMemberInput {
  name?: string;
  contactId?: string | null;
  role?: string;
  department?: string;
  team?: string;
  email?: string;
  phone?: string;
}

export interface CrewPickListsInput {
  departments?: string[];
  teams?: string[];
}

export const fetchCrew = async (projectId: string): Promise<CrewMember[]> => {
  const { data } = await api.get(`/projects/${projectId}/crew`);

  return data.map((row: CrewMember) => {
    // The list endpoint returns `ResolvedCrewMember` rows, which carry no `projectId` — merge in
    // the argument we already have, or the many-to-one relation never links and update/delete
    // build `/projects/undefined/crew/...` URLs.
    const member = Object.assign(new CrewMember(), row, { projectId });
    entityPool.insert(member);
    return member;
  });
};

export const createCrewMember = async (
  projectId: string,
  input: CrewMemberInput,
): Promise<CrewMember> => {
  const { data } = await api.post(`/projects/${projectId}/crew`, input);

  // The create endpoint returns the same `ResolvedCrewMember` shape the list endpoint does — no
  // `projectId` — so it needs the same backfill `fetchCrew` does, or update/delete build
  // `/projects/undefined/crew/...` URLs.
  const member = Object.assign(new CrewMember(), data, { projectId });
  entityPool.insert(member);

  return member;
};

export const updateCrewMember = async (
  member: CrewMember,
  update: CrewMemberInput,
): Promise<void> => {
  const { data } = await api.patch(`/projects/${member.projectId}/crew/${member._id}`, update);

  // The update endpoint returns the same `ResolvedCrewMember` shape list/create do — the
  // fallback-resolved name, not the client's raw input — and, like them, no `projectId`. Assigning
  // `update` here (the client's own input) is what let clearing the name field on an unfilled
  // position blank the row locally: no fallback applied until the next refetch. Assign from the
  // response, and keep the backfill so update/delete don't build `/projects/undefined/crew/...`.
  Object.assign(member, data, { projectId: member.projectId });
};

export const deleteCrewMember = async (member: CrewMember): Promise<void> => {
  await api.delete(`/projects/${member.projectId}/crew/${member._id}`);

  entityPool.delete(member);
};

export const reorderCrew = async (projectId: string, orderedIds: string[]): Promise<void> => {
  await api.patch(`/projects/${projectId}/crew/reorder`, { orderedIds });
};

/**
 * The server merges rather than replaces — it refuses to drop an option still assigned to a crew
 * member — so the response, not `input`, is the truth. Assigning the response back onto
 * `project.crewDepartments`/`crewTeams` is what keeps the picker in sync with what the server
 * actually kept, rather than with whatever this call happened to send.
 */
export const updateCrewPickLists = async (
  projectId: string,
  input: CrewPickListsInput,
): Promise<void> => {
  const { data } = await api.patch(`/projects/${projectId}/crew/pick-lists`, input);

  const project = Project.getOne(projectId);
  if (!project) return;

  project.crewDepartments = data.departments;
  project.crewTeams = data.teams;
};

/**
 * Persists a department or team that the project's pick-lists have never seen, so a value typed
 * into a picker survives a reload and is choosable again next time.
 *
 * Takes a whole `CrewMemberInput` rather than the two values, because that is what both callers
 * already hold — the crew modal after a save, and the crew screens after an inline commit — and
 * an update touching neither pick-list simply does nothing. Blanks are not options: clearing a
 * row's department sends `''`, which is a removal, not a new entry.
 *
 * Non-fatal by design at the call sites: the crew member has already been saved by the time this
 * runs, and the picker is already showing the new value optimistically; only the stored list is
 * at stake.
 */
export const persistNewCrewPickListValues = async (
  project: Project,
  update: CrewMemberInput,
): Promise<void> => {
  const pickListsUpdate: CrewPickListsInput = {};

  if (update.department && !project.crewDepartments.includes(update.department)) {
    pickListsUpdate.departments = [...project.crewDepartments, update.department];
  }

  if (update.team && !project.crewTeams.includes(update.team)) {
    pickListsUpdate.teams = [...project.crewTeams, update.team];
  }

  if (!pickListsUpdate.departments && !pickListsUpdate.teams) return;

  await updateCrewPickLists(project._id, pickListsUpdate);
};

/**
 * One inline commit from the crew list: save the field, then settle the pick-lists if the value
 * was a department or team the project had never seen.
 *
 * Here rather than in each screen so the two cannot drift — `Resources > Crew` and the Crew step
 * page both render the same list, and a screen that forgot the second half would let a typed
 * department vanish on the next reload. The returned promise rejects only when the crew member
 * itself failed to save, which is the part worth putting a toast on; the pick-list write is
 * fire-and-forget, exactly as it is after a modal save.
 */
export const saveCrewMemberField = async (
  project: Project,
  member: CrewMember,
  update: CrewMemberInput,
): Promise<void> => {
  await updateCrewMember(member, update);

  persistNewCrewPickListValues(project, update).catch(() => {
    // Non-fatal: the field was saved, and the list already shows the new value optimistically.
  });
};
